<template>
  <div class="customer-group-list-container customer-group-list-main customer-group-list-page--glass glass-tab-page customer-notes-list-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Customer Notes List']" />

    <div class="glass-tab-view glass-tab-view--flush customer-group-list-glass-shell">
      <div class="glass-tab-view__content customer-group-list-tab-content customer-group-list-page-stack">
        <div class="customer-group-list-header-block">
          <div class="customer-group-list-header-row">
            <div class="customer-group-list-header-row__text">
              <h2>Customer Notes</h2>
              <p class="customer-group-list-header-row__sub">
                View all customer notes saved across your company
              </p>
            </div>
          </div>

          <div class="customer-notes-list-filter-row">
            <LabelInputField
              input-id="customer-notes-search"
              label="Search"
              v-model="searchQuery"
              placeholder="Title, customer, notes..."
              flex
            />
          </div>
        </div>

        <div class="customer-group-list-glass-panel customer-group-list-main-card">
          <div class="consultant-glass-table-section customer-group-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table customer-notes-list-table">
                  <thead>
                    <tr>
                      <th class="customer-notes-list-col customer-notes-list-col--title">Title</th>
                      <th class="customer-notes-list-col customer-notes-list-col--customer">Customer</th>
                      <th class="customer-notes-list-col customer-notes-list-col--type">Type</th>
                      <th class="customer-notes-list-col customer-notes-list-col--priority">Priority</th>
                      <th class="customer-notes-list-col customer-notes-list-col--tags">Tags</th>
                      <th class="customer-notes-list-col customer-notes-list-col--followup">Follow-up</th>
                      <th class="customer-notes-list-col customer-notes-list-col--created">Created</th>
                      <th class="customer-notes-list-col customer-notes-list-col--private">Private</th>
                      <th class="customer-notes-list-col customer-notes-list-col--attachments">Files</th>
                      <th class="customer-notes-list-col customer-notes-list-col--notes">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="10" class="consultant-glass-table-no-records customer-group-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="listError">
                      <td colspan="10" class="consultant-glass-table-no-records customer-group-list-no-records">
                        {{ listError }}
                      </td>
                    </tr>
                    <tr v-else-if="filteredNotes.length === 0">
                      <td colspan="10" class="consultant-glass-table-no-records customer-group-list-no-records">
                        No notes to show
                      </td>
                    </tr>
                    <tr
                      v-for="(note, idx) in filteredNotes"
                      :key="note.id || idx"
                      class="consultant-glass-table-row customer-group-list-row"
                      :style="{ '--cgl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                    >
                      <td class="customer-notes-list-col customer-notes-list-col--title">{{ note.title }}</td>
                      <td class="customer-notes-list-col customer-notes-list-col--customer">
                        {{ note.customerName || '-' }}
                      </td>
                      <td class="customer-notes-list-col customer-notes-list-col--type">{{ note.noteType || '-' }}</td>
                      <td class="customer-notes-list-col customer-notes-list-col--priority">{{ note.priority || '-' }}</td>
                      <td class="customer-notes-list-col customer-notes-list-col--tags">
                        <div v-if="note.tags && note.tags.length" class="customer-notes-list-tags">
                          <span
                            v-for="tag in note.tags"
                            :key="`note-list-tag-${note.id}-${tag.id}`"
                            class="customer-notes-list-tag"
                            :style="tagStyle(tag)"
                          >
                            <span
                              class="customer-notes-list-tag__strip"
                              :style="{ backgroundColor: tag.color || '#6366f1' }"
                              aria-hidden="true"
                            ></span>
                            <span class="customer-notes-list-tag__label">{{ tag.name }}</span>
                          </span>
                        </div>
                        <span v-else>-</span>
                      </td>
                      <td class="customer-notes-list-col customer-notes-list-col--followup">
                        {{ formatDate(note.followUpDate) }}
                      </td>
                      <td class="customer-notes-list-col customer-notes-list-col--created">
                        {{ formatDateTime(note.createdAt) }}
                      </td>
                      <td class="customer-notes-list-col customer-notes-list-col--private">
                        <span v-if="note.isPrivate" class="customer-notes-list-private-badge">Yes</span>
                        <span v-else>-</span>
                      </td>
                      <td class="customer-notes-list-col customer-notes-list-col--attachments">
                        {{ note.attachments?.length || 0 }}
                      </td>
                      <td class="customer-notes-list-col customer-notes-list-col--notes">
                        <div class="customer-notes-list-note-preview">{{ note.notes }}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/CustomerGroupList.css';
import '../styles/CustomerNotesList.css';
import customerNoteService from '@/services/customerNote.service.js';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';

export default {
  name: 'CustomerNotesList',
  components: {
    BrucdcumHeader,
    LabelInputField,
  },
  data() {
    return {
      notes: [],
      loading: false,
      listError: '',
      searchQuery: '',
      companyId: this.$route.params.id,
    };
  },
  computed: {
    filteredNotes() {
      const query = (this.searchQuery || '').trim().toLowerCase();
      if (!query) return this.notes;

      return this.notes.filter((note) => {
        const haystack = [
          note.title,
          note.customerName,
          note.noteType,
          note.priority,
          note.notes,
          ...(note.tags || []).map((tag) => tag.name),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    },
  },
  async mounted() {
    await this.fetchNotes();
  },
  methods: {
    tagStyle(tag) {
      const color = tag?.color || '#6366f1';
      return {
        borderColor: `${color}55`,
        color,
        background: `${color}14`,
      };
    },
    formatDate(value) {
      if (!value) return '-';
      const text = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        const [year, month, day] = text.split('-');
        return `${month}/${day}/${year}`;
      }
      const date = new Date(text);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      }
      if (text.includes('T')) {
        const datePart = text.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          const [year, month, day] = datePart.split('-');
          return `${month}/${day}/${year}`;
        }
      }
      return text;
    },
    formatDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return this.formatDate(value);
      return date.toLocaleString();
    },
    async fetchNotes() {
      this.loading = true;
      this.listError = '';
      try {
        const response = await customerNoteService.getCompanyCustomerNotes(this.companyId);
        this.notes = response?.data?.data || [];
      } catch (error) {
        logger.error('Failed to load company customer notes:', error);
        this.listError =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Failed to load customer notes.';
        this.notes = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
